import type { Snowflake } from "@greenvoice/shared";
import type { VoiceConnState } from "../lib/voice";

/**
 * Quem manda na call do escritório 2D — a decisão, sem efeitos.
 *
 * Vive fora do `OfficeSpace` porque era aqui que estava o defeito de 2026-09-02
 * (*"clico no chat, volto, e saí da call — e o cabeçalho continua a dizer que
 * estou nela"*), e lá dentro não havia como lhe pegar: são três ramos entalados
 * entre o Phaser, a store e o motor de voz. Aqui são funções puras com uma
 * suite em cima; o componente fica só com os efeitos (entrar, sair, marcar).
 *
 * O vocabulário, uma vez:
 *
 * - **sala** — a sala de cargo onde o avatar está de pé (Vendas, TI, Reunião…).
 *   Casa com o canal de VOZ do mesmo nome; o corredor é zona neutra.
 * - **dona** — a call que ESTE escritório abriu. Só se desligam calls nossas:
 *   uma que o utilizador juntou à mão pela vista de chat não é da sala e
 *   continua a tocar quando ele sai dela.
 * - **motor** — o estado real da ligação. A store guarda a INTENÇÃO e sobrevive
 *   a uma queda do media, por isso `voz.canal` aponta para o canal com o motor
 *   em baixo. Perguntar "estou nesta call?" à store é a origem do "mostra que
 *   estou na call e não estou".
 */

/** O que a store diz (canal) e o que o motor diz (ligação real). */
export type Voz = {
  readonly canal: Snowflake | null;
  readonly motor: VoiceConnState;
};

/**
 * A ligação está de pé — ou a levantar-se.
 *
 * NÃO é o `voiceLive` do `useVoice`: esse responde "posso mexer no microfone?" e
 * exclui de propósito o `connecting`. Aqui a pergunta é "há ligação a esta call,
 * ou vale a pena começar uma?", e uma ligação em curso conta — senão entrava-se
 * duas vezes por cima de si própria.
 *
 * `idle` e `failed` ficam de fora: motor parado é call nenhuma, esteja a store
 * a dizer o que disser.
 */
export function chamadaDePe(motor: VoiceConnState): boolean {
  return motor === "connected" || motor === "connecting" || motor === "reconnecting";
}

/**
 * Estou DENTRO da sala e FORA da call dela? — é esta a condição do botão
 * «Participar da chamada de voz».
 *
 * Chega-se aqui por quatro caminhos, e nenhum tinha porta de volta: sair da call
 * à mão sem sair da sala; nascer na sala a um F5 (nascer não abre a call, é
 * regra); estar noutra call, juntada pela vista de chat; e a sessão pendurada
 * com o motor em baixo. A única saída conhecida era ir ao corredor e voltar.
 */
export function foraDaCall(salaVoz: Snowflake | null, voz: Voz): boolean {
  return salaVoz !== null && !(voz.canal === salaVoz && chamadaDePe(voz.motor));
}

/** O que fazer à chamada, e quem fica dona dela a seguir. */
export type PlanoDeSala =
  | { readonly accao: "entrar"; readonly canal: Snowflake; readonly dona: Snowflake }
  | { readonly accao: "sair"; readonly dona: null }
  | { readonly accao: "nada"; readonly dona: Snowflake | null };

/**
 * Entrar numa sala, mudar de sala, voltar ao corredor — a reconciliação.
 *
 * `salaVoz` já vem filtrado pela permissão de CONNECT: sem ela é `null`, como
 * se a sala não tivesse call. (O avatar "entrava" na sala e o servidor
 * respondia 403 ao pedido da chamada.)
 *
 * O `chamadaDePe` na condição de "já lá estou" é o que faz voltar a entrar numa
 * sala RELIGAR uma call morta. Comparar só os canais dizia "já lá estás" com o
 * motor em baixo, e entrar na sala não fazia rigorosamente nada.
 */
export function planoDeSala(salaVoz: Snowflake | null, voz: Voz, dona: Snowflake | null): PlanoDeSala {
  if (salaVoz !== null) {
    if (voz.canal === salaVoz && chamadaDePe(voz.motor)) return { accao: "nada", dona: salaVoz };
    return { accao: "entrar", canal: salaVoz, dona: salaVoz };
  }
  // Corredor: só se desliga a call se for a nossa e for a que está a tocar.
  if (dona !== null && voz.canal === dona) return { accao: "sair", dona: null };
  return { accao: "nada", dona: null };
}

/**
 * Voltar ao escritório (o spawn chega com `enter=false`): a propriedade da call
 * sobrevive à remontagem se a chamada a tocar ainda for a nossa.
 *
 * Antes apagava-se sempre, e o escritório voltava sem saber que a call da sala
 * era dele — sair para o corredor deixava de a desligar.
 */
export function donaAoVoltar(dona: Snowflake | null, canalAtivo: Snowflake | null): Snowflake | null {
  return dona !== null && dona === canalAtivo ? dona : null;
}

/** Fechar o escritório: desligar a call, ou não, e quem fica dona. */
export type PlanoDeFecho = {
  readonly desligar: boolean;
  readonly dona: Snowflake | null;
};

/**
 * O escritório desmontou — mas desmontar não é sair.
 *
 * Carregar em «Discord» destrói este componente exactamente como trocar de
 * servidor destrói: o cleanup é o mesmo. A diferença está na store — continuar
 * no mesmo servidor (`aindaNesteServidor`) quer dizer que só se mudou de VISTA:
 * o avatar fica na sala, a posição fica guardada, e desligar-lhe a call era
 * pô-lo mudo num sítio onde ele continua a estar.
 */
export function planoDeFecho(
  dona: Snowflake | null,
  canalAtivo: Snowflake | null,
  aindaNesteServidor: boolean,
): PlanoDeFecho {
  if (aindaNesteServidor) return { desligar: false, dona };
  return { desligar: dona !== null && dona === canalAtivo, dona: null };
}

/** Retrato do ecrã da chamada num render: onde, qual, e se está a tapar o mundo. */
export type EcraDaCall = {
  /** O servidor deste escritório. Trocar de servidor apaga a `call` sem ninguém sair. */
  readonly servidor: Snowflake;
  /** A call que este escritório está a mostrar — `null` é call nenhuma. */
  readonly call: Snowflake | null;
  /** Expandida E à vista: o GreenVoice inteiro por cima do mundo 2D. */
  readonly cheia: boolean;
};

/**
 * *"quando eu clico em expandir na miniatura e saiu da call, o discord fecha"* —
 * a chamada acabou com o ecrã cheio à frente, e o que ele tinha à frente
 * desapareceu com ela.
 *
 * O ecrã cheio é o ÚNICO sítio do escritório onde vivem a barra de canais, a de
 * voz e o painel do utilizador (o `office__callnav`), e todo esse bloco está
 * pendurado no `callId`. Sair da chamada leva o `callId` a `null`, o bloco
 * desmonta inteiro — barra incluída — e o mundo 2D volta de estalo. Quem estava a
 * usar isto como Discord fica a olhar para um mapa que não pediu.
 *
 * A resposta não é remendar a barra dentro do escritório: é reconhecer a VISTA
 * que ele escolheu. Ecrã cheio quer dizer "estou no GreenVoice, não no mapa" —
 * então a call a acabar entrega a vista de chat, que é o mesmo painel mais a
 * conversa. O avatar sai do mapa (é o que o botão «Discord» já faz, e a posição
 * fica guardada), e um clique em «Escritório 2D» traz tudo de volta.
 *
 * Duas portas fechadas de propósito:
 *
 * - **encolhida ou fechada** (`cheia` falso) — o mapa já estava à vista, ninguém
 *   pediu para sair dele; sair da call não pode mudar de vista às escondidas.
 * - **troca de servidor** — o `callId` é derivado e compara o `serverId`, por
 *   isso a call do escritório anterior desaparece daqui SEM ninguém desligar
 *   nada. Sem esta guarda, saltar de servidor com o ecrã cheio aberto atirava
 *   para o chat, e o escritório novo nunca chegava a abrir.
 */
export function saiuDeEcraCheio(antes: EcraDaCall, agora: EcraDaCall): boolean {
  if (antes.servidor !== agora.servidor) return false;
  return antes.cheia && antes.call !== null && agora.call === null;
}
